import React, { useCallback, type FC, useEffect, useContext } from 'react';
import { css } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ButtonGroup } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';

import type { WithFlagsProps } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import {
	ExperienceStart,
	ExperienceSuccess,
	ExperienceTrackerContext,
	VIEW_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE,
	SUBMIT_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE,
} from '@confluence/experience-tracker';
import { isUnauthorizedError } from '@confluence/error-boundary';

import { useIsExternalCollaborator } from '../../entry-points/useIsExternalCollaborator';
import { useGuestRequestToUpgradeStatus } from '../../entry-points/useGuestRequestToUpgradeStatus';

import { GuestRequestUpgradeButton } from './GuestRequestUpgradeButton';
import { useGuestUpgradeFlowExperiment } from './useGuestUpgradeFlowExperiment';
import { useSubmitGuestUpgradeRequest } from './useSubmitGuestUpgradeRequest';
import { UpgradeStatus } from './types';

const TRACK_EVENT = 'sendTrackEvent';
const ANALYTICS_SOURCE = 'sideNav';

export const i18n = defineMessages({
	body: {
		id: 'external-collab-ui.guest-request-to-upgrade-page-tree.body',
		defaultMessage: 'You are a guest on this site with limited access.',
		description:
			'This text appears next to the action to request an upgrade, explaining that the user is a guest with limited capabilities',
	},
	pendingBody: {
		id: 'external-collab-ui.guest-request-to-upgrade-page-tree.pending-body',
		defaultMessage: 'You are a guest on this site.',
		description:
			'This text appears next to the disabled button, explaining that the upgrade request is pending',
	},

	flagSuccessTitle: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-success-title',
		defaultMessage: 'Licensed user request sent!',
		description:
			'Flag notification title informing the guest that their request to be upgraded to a licensed user was successfully submitted',
	},
	flagSuccessDescription: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-success-description',
		defaultMessage: 'If approved, you’ll get a confirmation message from admin',
		description: 'Flag notification description informing guest that request was submitted',
	},

	flagErrorTitle: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-error-title',
		defaultMessage: 'Request not sent',
		description: 'Flag notification title informing guest that request encountered an error',
	},
	flagErrorDescription: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-error-description',
		defaultMessage: 'Refresh the page to resubmit your request, or try again later.',
		description: 'Flag notification description informing guest that request encountered an error',
	},
});

const SECTION_LABEL_ARIA_ID = '__guest-request-to-upgrade-to-user-aria-label-id';

const sectionStyles = css({
	boxSizing: 'border-box',
	maxWidth: '344px',
	padding: token('space.100', '8px'),
	margin: token('space.100', '8px'),
	fontWeight: token('font.weight.semibold', '600'),
	color: token('color.text.subtlest'),
});

const buttonGroupContainerStyles = xcss({
	paddingTop: 'space.200',
});

const GuestRequestToUpgradeSideNavComponent: FC<WithFlagsProps> = ({ flags }) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();

	const showErrorFlag = useCallback(
		(submissionError: Error) => {
			void flags.showErrorFlag({
				title: <FormattedMessage {...i18n.flagErrorTitle} />,
				description: <FormattedMessage {...i18n.flagErrorDescription} />,
			});
			experienceTracker.fail({
				name: SUBMIT_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE,
				error: submissionError,
			});
		},
		[flags, experienceTracker],
	);

	const onGuestUpgradeRequestSubmitted = useCallback(() => {
		void flags.showSuccessFlag({
			title: <FormattedMessage {...i18n.flagSuccessTitle} />,
			description: <FormattedMessage {...i18n.flagSuccessDescription} />,
		});
		experienceTracker.succeed({
			name: SUBMIT_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE,
		});
	}, [flags, experienceTracker]);

	const handleOnSubmit = (error?: Error) => {
		createAnalyticsEvent({
			type: TRACK_EVENT,
			data: {
				action: !!error ? 'failed' : 'requested',
				actionSubject: 'productAccess',
				source: ANALYTICS_SOURCE,
				attributes: {
					touchpointId: ANALYTICS_SOURCE,
				},
			},
		}).fire();

		if (!error) {
			onGuestUpgradeRequestSubmitted();
		} else {
			showErrorFlag(error);
		}
	};

	const [
		submitGuestUpgradeRequest,
		{ loading: guestUpgradeRequestIsLoading, isSubmitted: guestUpgradeSubmitted },
	] = useSubmitGuestUpgradeRequest({ onCompleted: handleOnSubmit });

	const {
		upgradeStatus,
		upgradeStatusReason,
		loading: permissionLoading,
		error: permissionsError,
	} = useGuestRequestToUpgradeStatus(!isGuest);

	const permissionDenied = upgradeStatus === UpgradeStatus.NOT_ELIGIBLE_TO_UPGRADE_AT_ALL;

	const requestPending =
		guestUpgradeSubmitted || upgradeStatus === UpgradeStatus.AN_UPGRADE_REQUEST_IS_PENDING;

	const { shouldRenderExperiment } = useGuestUpgradeFlowExperiment(isGuest, requestPending);

	const shouldRenderUpgradeSection = shouldRenderExperiment && !permissionDenied;

	useEffect(() => {
		if (!permissionLoading) {
			if (permissionsError && !isUnauthorizedError(permissionsError)) {
				experienceTracker.stopOnError({
					name: VIEW_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE,
					error: permissionsError,
				});
			} else if (permissionDenied) {
				experienceTracker.abort({
					name: VIEW_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE,
					reason: upgradeStatusReason ?? UpgradeStatus.NOT_ELIGIBLE_TO_UPGRADE_AT_ALL,
				});
			}
		}
	}, [
		permissionDenied,
		permissionLoading,
		permissionsError,
		experienceTracker,
		upgradeStatusReason,
	]);

	const submitGuestUpgradeRequestWithAnalytics = (
		_: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		const analyticsAttributes = {
			...analyticsEvent.payload.attributes,
			touchpointId: ANALYTICS_SOURCE,
		};
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				...analyticsEvent.payload,
				actionSubjectId: 'submitGuestUpgradeRequest',
				source: 'guestRequestAccess',
				attributes: analyticsAttributes,
			},
		}).fire();
		experienceTracker.start({
			name: SUBMIT_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE,
		});
		void submitGuestUpgradeRequest();
	};

	if (permissionLoading || !shouldRenderUpgradeSection) {
		return null;
	}

	// https://data-portal.internal.atlassian.com/analytics/registry/68335
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'shown',
			actionSubject: 'button',
			actionSubjectId: 'submitGuestUpgradeRequest',
			source: ANALYTICS_SOURCE,
			attributes: {
				touchpointId: ANALYTICS_SOURCE,
				isPending: requestPending,
			},
		},
	}).fire();

	return (
		<section aria-labelledby={SECTION_LABEL_ARIA_ID} css={sectionStyles}>
			<ExperienceSuccess name={VIEW_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE} />
			<p>
				{requestPending ? (
					<FormattedMessage {...i18n.pendingBody} />
				) : (
					<FormattedMessage {...i18n.body} />
				)}
			</p>
			<Box xcss={buttonGroupContainerStyles}>
				<ButtonGroup>
					<GuestRequestUpgradeButton
						appearance="default"
						requestPending={requestPending}
						loading={guestUpgradeRequestIsLoading}
						onClick={submitGuestUpgradeRequestWithAnalytics}
					/>
				</ButtonGroup>
			</Box>
		</section>
	);
};

const GuestRequestToUpgradeSideNavWithExperienceTracking: FC<WithFlagsProps> = (props) => (
	<>
		<ExperienceStart name={VIEW_GUEST_REQUEST_TO_UPGRADE_SIDE_NAV_EXPERIENCE} timeout={3000} />
		<GuestRequestToUpgradeSideNavComponent {...props} />
	</>
);

export const GuestRequestToUpgradeSideNav = withFlags(
	GuestRequestToUpgradeSideNavWithExperienceTracking,
);
